/**
 * Cat & Mouse — what makes a glyph "the same letter" (specs/cat-mouse-mode.md §6, §9).
 *
 * The mode's premise is a case pair: `K` and `k` are two drawings of one letter. Only the Latin
 * alphabet works that way, so this file carries the three shapes the idea actually takes:
 *
 *   A  **Two glyphs, one letter** — Latin case (`K`/`k`), Japanese kana (`か`/`カ`). A membership
 *      *list*: the pack authors both forms and nothing is computed.
 *   B  **Alone, and inside** — Korean (`ㄱ` in `가`), Thai (`ก` in `เก`). A membership *predicate*:
 *      the pack authors the bare letter and a `contains` strategy, and the surface forms are
 *      generated, because 11 172 Hangul blocks are not a data file.
 *   C  **Singleton, defined by its rivals** — Thai's consonants, and Latin's `b`/`d`/`p`/`q`. One
 *      form, and the whole difficulty lives in `decoyPreferClasses`.
 *
 * One scene, two membership strategies, and a decoy field all three need.
 *
 * Phaser-free. Imports `cellsOf`/`isDenseScript` from the Ice Maze's segmenter rather than
 * re-deriving them — that file already merges Thai leading vowels rightwards so `เก` is one cell,
 * and it is the single largest reason this mode is affordable in `th` at all (§8.4).
 */
import type { Pack, PackClass, PackWord } from '@/data/packs/types';
import { cellsOf, isDenseScript } from '@/game/maze/segment';
import { blocksForInitial, blocksForMedial, hasJamo } from './hangul';

export { isDenseScript };

/**
 * How a class decides whether a glyph is "it", beyond its authored forms. Absent → shape A or C,
 * the forms are the whole answer.
 */
export type Contains = 'ko-initial' | 'ko-medial' | 'th-base';

export interface LetterClass {
  id: string;
  /** What the door wears — `"K k"`, `"か カ"`, `"ㄱ"`, `"ก"`. Rendered, never translated. */
  label: string;
  /** Teaching order, NOT the alphabet (§8.2.1). Lowest first. */
  rank: number;
  /** Every authored glyph of this letter, in `rank`-then-author order. */
  forms: string[];
  /** Shape-B predicate, or null for A and C. */
  contains: Contains | null;
  /** Class ids this letter is confused with — the preferred decoy source (§8.3). */
  prefer: string[];
  /** Stable mastery ids, one per form (`K` and `k` are learned separately — §2.1). */
  formIds: string[];
}

/**
 * Thai surface forms: the base consonant wearing a vowel.
 *
 * **Only non-spacing and leading vowels.** `กา` (SARA AA) is two grapheme clusters and would need
 * two cells, which breaks the one-glyph-per-cell rule the whole board rests on. `กิ` (above), `กุ`
 * (below), `กำ` (SARA AM, which the segmenter binds) and `เก`/`ไก` (leading, which the segmenter
 * merges rightwards) are all exactly one cell.
 *
 * That constraint is also the pedagogy: a vowel written *before* the consonant you are hunting for
 * is the visual habit Thai reading requires and Latin reading never does (§9.4).
 */
const THAI_VOWEL_TEMPLATES = ['เ{c}', 'ไ{c}', '{c}ิ', '{c}ุ', '{c}ำ'];

/**
 * Every glyph that IS this letter — authored forms first, then the generated ones for shape B.
 *
 * Generated forms are capped: a class needs enough surface variety that the child cannot match on
 * "the one odd-looking cell", not every syllable the script can spell.
 */
const FORMS_CACHE = new Map<string, string[]>();

export function formsOf(cls: LetterClass, lang: string, variants = 4): string[] {
  // Memoized because a single board deals this once per target and once per decoy class, and the
  // shape-B branch runs `cellsOf` — which builds an `Intl.Segmenter` — for every generated Thai
  // form. Uncached, dealing the ladder across ten languages took longer than a 5s test budget; the
  // answer for a class never changes, so caching it is free.
  const key = `${lang}|${cls.id}|${variants}`;
  const hit = FORMS_CACHE.get(key);
  if (hit) return hit;
  const out = [...cls.forms];
  const base = cls.forms[0];
  if (!base) return out;
  if (cls.contains === 'ko-initial') out.push(...blocksForInitial(base, variants));
  else if (cls.contains === 'ko-medial') out.push(...blocksForMedial(base, variants));
  else if (cls.contains === 'th-base') {
    for (const t of THAI_VOWEL_TEMPLATES.slice(0, variants)) {
      const form = t.replace('{c}', base);
      // Belt and braces: a template that did not stay one cell must never reach a board.
      if (cellsOf(form, lang).length === 1) out.push(form);
    }
  }
  const forms = [...new Set(out)];
  FORMS_CACHE.set(key, forms);
  return forms;
}

/**
 * Is `glyph` this letter?
 *
 * Shape A and C answer from the forms list. Shape B answers from the predicate, and never from
 * string containment — `'가'.normalize('NFD').includes('ㄱ')` is `false`, which is the trap
 * `hangul.ts` exists for.
 */
export function matches(glyph: string, cls: LetterClass): boolean {
  if (cls.forms.includes(glyph)) return true;
  const base = cls.forms[0];
  if (!base) return false;
  switch (cls.contains) {
    case 'ko-initial': return hasJamo(glyph, base, 'initial');
    case 'ko-medial': return hasJamo(glyph, base, 'medial');
    // A Thai cluster is the consonant plus marks that hang off it, so containment IS the answer
    // here — unlike Hangul, where the jamo inside a block is a different codepoint from the one on
    // the chart.
    case 'th-base': return glyph.includes(base);
    default: return false;
  }
}

/**
 * Build the mode's classes from a `letters.<lang>.json` pack.
 *
 * The pack is a plain `Pack`: its `classes` are the letters and its words are the forms. Nothing
 * in the schema is letter-specific except `decoyPreferClasses`, which is the mirror of a field that
 * already existed (§6).
 *
 * Classes are returned in `rank` order — the order the letters are *introduced*, which is not the
 * alphabet (§8.2.1) — because the active window (§8.2) opens them in exactly that order.
 */
export function classesOf(pack: Pack): LetterClass[] {
  const words: PackWord[] = pack.sets.flatMap((s) => s.words);
  const byClass = new Map<string, PackWord[]>();
  for (const w of words) {
    const list = byClass.get(w.class) ?? [];
    list.push(w);
    byClass.set(w.class, list);
  }

  const out: LetterClass[] = [];
  for (const pc of pack.classes as PackClass[]) {
    const ws = (byClass.get(pc.id) ?? []).slice().sort(rankThenIndex);
    if (ws.length === 0) continue;
    const contains = ws.map((w) => w.contains).find((c) => typeof c === 'string');
    out.push({
      id: pc.id,
      label: pc.label,
      rank: Number(ws[0].rank ?? 9999),
      forms: ws.map((w) => w.text),
      contains: (contains as Contains | undefined) ?? null,
      prefer: pc.decoyPreferClasses ?? [],
      formIds: ws.map((w, i) => String(w.id ?? `${pc.id}-${i}`)),
    });
  }
  return out.sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id));
}

function rankThenIndex(a: PackWord, b: PackWord): number {
  return Number(a.rank ?? 9999) - Number(b.rank ?? 9999);
}
